import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@eriko/ui/src/components/ui/card';
import { Loader2 } from 'lucide-react';
import {
  useUkSaEstimate,
  useMtdStatus,
  useCt600,
  useUkDeadlines,
  useUkFilingDrafts,
  useMarkUkFiled,
  useAdminCisSubcontractors,
} from '../../../../hooks/useUkTax';

function formatPounds(minor: number): string {
  return `\u00A3${(minor / 100).toLocaleString('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function filingTypeLabel(type: string): string {
  const map: Record<string, string> = {
    SCHEDULE_C: 'Schedule C',
    FORM_1099: 'Form 1099-NEC',
    VAT_RETURN: 'VAT Return',
    SA103: 'SA103 Self Assessment',
    CIS300: 'CIS Monthly Return',
    CT600_SUMMARY: 'CT600 Corporation Tax',
  };
  return map[type] ?? type;
}

function statusLabel(s: string): string {
  const map: Record<string, string> = {
    DRAFT: 'Draft',
    READY: 'Ready',
    FILED_VIA_INTEGRATION: 'Filed',
    FILED_EXTERNALLY: 'Filed',
    ARCHIVED: 'Archived',
  };
  return map[s] ?? s;
}

export function UkTaxPanel({ orgId }: { orgId: string }) {
  const { data: saEstimate, isLoading: loadingSa } = useUkSaEstimate(orgId);
  const { data: mtd, isLoading: loadingMtd } = useMtdStatus(orgId);
  const { data: ct600, isLoading: loadingCt600 } = useCt600(orgId);
  const { data: deadlines, isLoading: loadingDeadlines } = useUkDeadlines(orgId);
  const { data: filingDrafts, isLoading: loadingDrafts } = useUkFilingDrafts(orgId);
  const { data: cisSubcontractors, isLoading: loadingCis } = useAdminCisSubcontractors(orgId);
  const markFiled = useMarkUkFiled(orgId);

  const isLoading = loadingSa || loadingMtd || loadingCt600 || loadingDeadlines || loadingDrafts || loadingCis;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-surface-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upcoming UK Deadlines */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upcoming UK Deadlines</CardTitle>
        </CardHeader>
        <CardContent>
          {!deadlines || deadlines.length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming deadlines.</p>
          ) : (
            <div className="space-y-2">
              {deadlines.map((d) => (
                <div key={d.filingType} className="flex items-center justify-between text-sm">
                  <span className="text-surface-700">{d.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-surface-500">{formatDate(d.dueDate)}</span>
                    <Badge
                      variant={
                        d.daysUntilDue <= 7
                          ? 'danger'
                          : d.daysUntilDue <= 30
                            ? 'warning'
                            : 'success'
                      }
                    >
                      {d.daysUntilDue} days
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SA103 Summary / Readiness */}
      {saEstimate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Self Assessment — {saEstimate.taxYear}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center mb-4">
              <div>
                <div className="text-lg font-bold text-surface-900">
                  {formatPounds(saEstimate.netProfitMinor)}
                </div>
                <div className="text-xs text-surface-500">Net Profit</div>
              </div>
              <div>
                <div className="text-lg font-bold text-surface-900">
                  {formatPounds(saEstimate.incomeTaxMinor)}
                </div>
                <div className="text-xs text-surface-500">Income Tax</div>
              </div>
              <div>
                <div className="text-lg font-bold text-surface-900">
                  {formatPounds(saEstimate.totalLiabilityMinor)}
                </div>
                <div className="text-xs text-surface-500">Total Liability</div>
              </div>
            </div>
            {saEstimate.breakdown.length > 0 && (
              <div className="space-y-1">
                {saEstimate.breakdown.map((b) => (
                  <div key={b.label} className="flex items-center justify-between text-sm py-0.5">
                    <span className="text-surface-600">{b.label}</span>
                    <span className="text-surface-900 font-medium">
                      {formatPounds(Math.abs(b.amountMinor))}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {saEstimate.paymentsOnAccount.length > 0 && (
              <div className="mt-4 pt-3 border-t border-surface-100">
                <div className="text-xs font-medium text-surface-500 mb-2">Payments on Account</div>
                {saEstimate.paymentsOnAccount.map((poa) => (
                  <div key={poa.dueDate} className="flex items-center justify-between text-sm py-0.5">
                    <span className="text-surface-600">{poa.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-surface-900 font-medium">
                        {formatPounds(poa.amountMinor)}
                      </span>
                      <span className="text-surface-500 text-xs">due {formatDate(poa.dueDate)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CT600 Summary (Ltd companies only) */}
      {ct600 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              CT600 Corporation Tax — {ct600.taxYear}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center mb-4">
              <div>
                <div className="text-lg font-bold text-surface-900">
                  {formatPounds(ct600.turnoverMinor)}
                </div>
                <div className="text-xs text-surface-500">Turnover</div>
              </div>
              <div>
                <div className="text-lg font-bold text-surface-900">
                  {formatPounds(ct600.profitBeforeTaxMinor)}
                </div>
                <div className="text-xs text-surface-500">Profit Before Tax</div>
              </div>
              <div>
                <div className="text-lg font-bold text-surface-900">
                  {formatPounds(ct600.corporationTaxMinor)}
                </div>
                <div className="text-xs text-surface-500">Corporation Tax</div>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm py-0.5">
                <span className="text-surface-600">Allowable Expenses</span>
                <span className="text-surface-900 font-medium">
                  {formatPounds(ct600.allowableExpensesMinor)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm py-0.5">
                <span className="text-surface-600">Capital Allowance (AIA)</span>
                <span className="text-surface-900 font-medium">
                  {formatPounds(ct600.capitalAllowanceMinor)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm py-0.5">
                <span className="text-surface-600">
                  CT Rate ({(ct600.corporationTaxRateBps / 100).toFixed(0)}%)
                </span>
                <span className="text-surface-900 font-medium">
                  {formatPounds(ct600.corporationTaxMinor)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CIS Subcontractors */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">CIS Subcontractors</CardTitle>
        </CardHeader>
        <CardContent>
          {!cisSubcontractors || cisSubcontractors.length === 0 ? (
            <p className="text-sm text-muted-foreground">No CIS subcontractors.</p>
          ) : (
            <div className="space-y-2">
              {cisSubcontractors.map((sub) => (
                <div key={sub.id} className="flex items-center justify-between text-sm py-1">
                  <div>
                    <span className="text-surface-700 font-medium">{sub.legalName}</span>
                    {sub.utrLast4 && (
                      <span className="text-surface-500 ml-2 text-xs">UTR: ******{sub.utrLast4}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={sub.cisVerifiedAt ? 'success' : 'warning'}
                    >
                      {sub.cisVerifiedAt ? 'Verified' : 'Unverified'}
                    </Badge>
                    {sub.cisDeductionRate != null && (
                      <span className="text-xs text-surface-500">
                        {(sub.cisDeductionRate / 100).toFixed(0)}% deduction
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* MTD-ITSA Eligibility */}
      {mtd && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">MTD-ITSA Eligibility</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-3">
              <Badge
                variant={
                  mtd.status.statusLevel === 'urgent'
                    ? 'danger'
                    : mtd.status.statusLevel === 'warning'
                      ? 'warning'
                      : 'info'
                }
              >
                {mtd.status.currentlyInScope ? 'In Scope' : mtd.status.willBeInScope ? 'Will Be In Scope' : 'Not In Scope'}
              </Badge>
              <span className="text-sm text-surface-600">{mtd.status.statusMessage}</span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex items-center justify-between py-0.5">
                <span className="text-surface-600">Recorded Income ({mtd.income.currentTaxYear})</span>
                <span className="text-surface-900 font-medium">{mtd.income.recordedIncomeFormatted}</span>
              </div>
              <div className="flex items-center justify-between py-0.5">
                <span className="text-surface-600">Projected Annual</span>
                <span className="text-surface-900 font-medium">{mtd.income.projectedIncomeFormatted}</span>
              </div>
              {mtd.phase && (
                <div className="flex items-center justify-between py-0.5">
                  <span className="text-surface-600">
                    HMRC mandate phase
                  </span>
                  <span className="text-surface-900 font-medium">
                    Phase {mtd.phase.phase} ({mtd.phase.thresholdFormatted})
                  </span>
                </div>
              )}
              {mtd.countdown.mandatoryDate && (
                <div className="flex items-center justify-between py-0.5">
                  <span className="text-surface-600">Mandatory from</span>
                  <span className="text-surface-900 font-medium">{mtd.countdown.mandatoryDate}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filing Drafts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filing Drafts</CardTitle>
        </CardHeader>
        <CardContent>
          {!filingDrafts || filingDrafts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No filing drafts yet.</p>
          ) : (
            <div className="space-y-2">
              {filingDrafts.map((d) => (
                <div key={d.id} className="flex items-center justify-between text-sm py-1">
                  <div>
                    <span className="text-surface-700 font-medium">{filingTypeLabel(d.filingType)}</span>
                    <span className="text-surface-500 ml-2">
                      {formatDate(d.periodStart)} — {formatDate(d.periodEnd)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {d.source && (
                      <Badge
                        variant="outline"
                        className="bg-purple-50 text-purple-700 border-purple-200"
                        title="Imported from an uploaded tax form (not computed from transactions)"
                      >
                        Imported
                      </Badge>
                    )}
                    <Badge
                      variant="outline"
                      className={
                        d.status === 'DRAFT'
                          ? ''
                          : d.status === 'READY'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-green-50 text-green-700 border-green-200'
                      }
                    >
                      {statusLabel(d.status)}
                    </Badge>
                    {(d.status === 'DRAFT' || d.status === 'READY') && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={markFiled.isPending}
                        onClick={() =>
                          markFiled.mutate({
                            draftId: d.id,
                            filedVia: 'manual',
                          })
                        }
                      >
                        Mark filed
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
